/**
 * The festive accent, darkened as needed so it reads on the light page (#F8FAFC). The dark page
 * uses the accent itself. Any colour an admin picks gets a readable ink on the light theme.
 */
const LIGHT_SURFACE = "#F8FAFC";
const MIN_CONTRAST = 4.5;

const channel = (hex: string, at: number) => parseInt(hex.slice(at, at + 2), 16) / 255;

const linear = (v: number) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);

function luminance(hex: string): number {
  return 0.2126 * linear(channel(hex, 1)) + 0.7152 * linear(channel(hex, 3)) + 0.0722 * linear(channel(hex, 5));
}

/** WCAG contrast ratio between two hex colours. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

function toHsl(hex: string): [number, number, number] {
  const r = channel(hex, 1), g = channel(hex, 3), b = channel(hex, 5);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h * 60, s, l];
}

function fromHsl(h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(v * 255).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
}

/** The accent itself when it reads on the light page, else the same hue made darker. */
export function inkOnLight(accent: string): string {
  const [h, s, l0] = toHsl(accent.toUpperCase());
  for (let l = l0; l > 0; l -= 0.005) {
    const ink = fromHsl(h, s, l);
    if (contrastRatio(ink, LIGHT_SURFACE) >= MIN_CONTRAST) return ink;
  }
  return "#000000";
}
